import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Stack,
  Box,
} from '@mui/material';
import { CreateSupplierAddressRequestDTO } from '@infrastructure/suppliers/dto/SupplierDTO';

interface AddressFormData {
  type: 'office' | 'production' | 'warehouse' | 'billing';
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  isPrimary: boolean;
}

const emptyForm = (): AddressFormData => ({
  type: 'office',
  name: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  country: 'USA',
  isPrimary: false,
});

export interface AddDraftAddressDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (address: CreateSupplierAddressRequestDTO) => void;
}

const AddDraftAddressDialog: React.FC<AddDraftAddressDialogProps> = ({ open, onClose, onSave }) => {
  const [formData, setFormData] = useState<AddressFormData>(emptyForm);

  const handleInputChange = (field: keyof AddressFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleClose = () => {
    setFormData(emptyForm());
    onClose();
  };

  const handleSubmit = () => {
    if (!formData.name.trim() || !formData.address.trim() || !formData.city.trim()) {
      return;
    }
    const payload: CreateSupplierAddressRequestDTO = {
      type: formData.type,
      name: formData.name.trim(),
      address: formData.address.trim(),
      city: formData.city.trim(),
      state: formData.state.trim(),
      zipCode: formData.zipCode.trim(),
      country: formData.country.trim() || 'USA',
      ...(formData.isPrimary ? { isPrimary: true } : {}),
    };
    onSave(payload);
    setFormData(emptyForm());
    onClose();
  };

  const isFormValid =
    formData.name.trim() && formData.address.trim() && formData.city.trim();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add address</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Address type</InputLabel>
            <Select
              value={formData.type}
              label="Address type"
              onChange={e => handleInputChange('type', e.target.value as AddressFormData['type'])}
            >
              <MenuItem value="office">Office</MenuItem>
              <MenuItem value="production">Production</MenuItem>
              <MenuItem value="warehouse">Warehouse</MenuItem>
              <MenuItem value="billing">Billing</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Address name"
            value={formData.name}
            onChange={e => handleInputChange('name', e.target.value)}
            placeholder="e.g., Main office"
            required
          />

          <TextField
            fullWidth
            label="Street address"
            value={formData.address}
            onChange={e => handleInputChange('address', e.target.value)}
            placeholder="123 Business Street"
            required
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="City"
              value={formData.city}
              onChange={e => handleInputChange('city', e.target.value)}
              required
            />
            <TextField
              fullWidth
              label="State"
              value={formData.state}
              onChange={e => handleInputChange('state', e.target.value)}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="ZIP code"
              value={formData.zipCode}
              onChange={e => handleInputChange('zipCode', e.target.value)}
            />
            <TextField
              fullWidth
              label="Country"
              value={formData.country}
              onChange={e => handleInputChange('country', e.target.value)}
            />
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={formData.isPrimary}
                onChange={e => handleInputChange('isPrimary', e.target.checked)}
                color="primary"
              />
            }
            label="Set as primary address"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!isFormValid}>
          Add address
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddDraftAddressDialog;
